// Builds the ordered "order progress" step list consumed by <StatusBar>.
// Names and colors are read from the backend ORDER_STATUS reference list
// (general store `orderStatus`) so the steps always match the backend source
// of truth — never hardcode status names/colors here.

type StatusRef = { code: string; sentName?: string; receivedName?: string; name?: string; color?: string }

export type OrderStatusStep = { code: string; name: string; color?: string }

// Canonical happy-path lifecycle, ordered per de-order-service ORDER_STATUS.
// CLOSED is intentionally absent: nothing in de-order-service ever sets it, so
// delivery ends the visible pipeline at DELIVERED. Should an order actually
// carry CLOSED, the step is appended on the fly below.
const ORDER_LIFECYCLE = [
  "REGISTERED",
  "REVIEWED",
  "APPROVED",
  "AUTHORIZED",
  "PREPARING",
  "DELIVERING",
  "DELIVERED",
] as const

// Terminal/branch statuses that end the flow early. The value is the last
// happy-path step reached before the order branched off, or null when the
// status can occur at any point (only the terminal step is then appended).
const TERMINAL_AFTER: Record<string, string | null> = {
  REVIEW_REJECTED: "REGISTERED",
  REJECTED       : "REVIEWED",
  CANCELED       : null,
  AUTO_CANCELED  : null,
}

// Since 2026-11-04 de-order-service keeps order status and delivery status
// apart: order.orderStatus ends at AUTHORIZED, order.deliveryStatus (read from
// the delivery notes) says where the goods are, and delivery completion stamps
// order.deliveredDate. The progress bar is a journey view, so it maps the
// delivery status onto its last three steps.
const DELIVERY_STATUS_TO_ORDER_STEP: Record<string, string> = {
  CREATED         : "PREPARING",
  ASSIGNED        : "PREPARING",
  PENDING_HANDOVER: "PREPARING",
  LOADED          : "PREPARING",
  DELIVERING      : "DELIVERING",
  DELIVERED       : "DELIVERED",
}

// The delivery steps are not order statuses any more, so their names come from
// the ORDER_DELIVERY_STATUS reference list (general store `orderDeliveryStatus`).
const DELIVERY_STEP_NAME_CODE: Record<string, string> = {
  PREPARING : "CREATED",
  DELIVERING: "DELIVERING",
  DELIVERED : "DELIVERED",
}

// Older backend without `deliveryStatus`: map the order's own delivery note.
const DN_STATUS_TO_ORDER_STEP: Record<string, string> = {
  DRAFT           : "PREPARING",
  ASSIGNED        : "PREPARING",
  PENDING_HANDOVER: "PREPARING",
  LOADED          : "PREPARING",
  DELIVERING      : "DELIVERING",
  DELIVERED       : "DELIVERED",
}

type OrderLike = {
  orderStatus?: string
  deliveryStatus?: string | null
  deliveredDate?: string | Date | null
  deliveryNote?: { deliveryNoteStatus?: string | null } | null
}

/**
 * Effective status for the progress bar. Only elevates AUTHORIZED orders using
 * the delivery status (or, on an older backend, the delivery-note state) and
 * deliveredDate; every other status (terminal, pre-fulfillment, CLOSED) is
 * returned as-is.
 */
export function deriveEffectiveOrderStatus(order: OrderLike | null | undefined): string {
  const status = order?.orderStatus || ""
  if (status !== "AUTHORIZED") return status
  if (order?.deliveredDate) return "DELIVERED"
  if (order?.deliveryStatus !== undefined) {
    return (order.deliveryStatus && DELIVERY_STATUS_TO_ORDER_STEP[order.deliveryStatus]) || status
  }
  const dnStatus = order?.deliveryNote?.deliveryNoteStatus
  return (dnStatus && DN_STATUS_TO_ORDER_STEP[dnStatus]) || status
}

/**
 * Derive the progress steps + current status for an order.
 *
 * @param order        the order detail object (needs `orderStatus`)
 * @param statusRefs   backend ORDER_STATUS reference list (general.orderStatus)
 * @param opts.perspective  "sent" (default) or "received" — which label to show
 * @param opts.upTo    truncate the happy-path at this code (e.g. "AUTHORIZED"
 *                     for the buyer's purchase view that ends before fulfillment)
 * @param opts.deliveryRefs  backend ORDER_DELIVERY_STATUS reference list
 *                     (general.orderDeliveryStatus), names the delivery steps
 */
export function buildOrderStatusSteps(
  order: OrderLike | null | undefined,
  statusRefs: StatusRef[] | undefined,
  opts?: { perspective?: "sent" | "received"; upTo?: string; from?: string; deliveryRefs?: StatusRef[] },
): { steps: OrderStatusStep[]; currentStatus: string; pastEnd: boolean } {
  const current = deriveEffectiveOrderStatus(order)
  const refs = statusRefs || []

  // An older backend still lists PREPARING / DELIVERING / DELIVERED as order statuses; a newer one names them in
  // the delivery reference list.
  const refOf = (code: string) =>
    refs.find((r) => r.code === code) ||
    (DELIVERY_STEP_NAME_CODE[code] ? opts?.deliveryRefs?.find((r) => r.code === DELIVERY_STEP_NAME_CODE[code]) : undefined)
  const nameOf = (code: string) => {
    const ref = refOf(code)
    if (!ref) return code
    if (ref.name) return ref.name
    return opts?.perspective === "received"
      ? ref.receivedName || ref.sentName || code
      : ref.sentName || code
  }
  const stepOf = (code: string): OrderStatusStep => ({ code, name: nameOf(code), color: refOf(code)?.color })

  let base: string[] = [...ORDER_LIFECYCLE]
  if (opts?.upTo) {
    const i = base.indexOf(opts.upTo)
    if (i >= 0) base = base.slice(0, i + 1)
  }
  // `from`: start the visible pipeline at a step (Борлуулалт detail shows the delivery stages from AUTHORIZED on).
  if (opts?.from) {
    const i = base.indexOf(opts.from)
    if (i >= 0) base = base.slice(i)
  }

  const steps: OrderStatusStep[] = []

  // DRAFT is pre-submission — only surfaced while the order is still a draft.
  if (current === "DRAFT") steps.push(stepOf("DRAFT"))

  if (current in TERMINAL_AFTER) {
    const anchor = TERMINAL_AFTER[current]
    if (anchor) {
      const i = base.indexOf(anchor)
      base.slice(0, i + 1).forEach((c) => steps.push(stepOf(c)))
    }
    steps.push(stepOf(current))
    return { steps, currentStatus: current, pastEnd: false }
  }

  base.forEach((c) => steps.push(stepOf(c)))
  if (current === "CLOSED" && !opts?.upTo) steps.push(stepOf("CLOSED"))
  // With `upTo`, an order already beyond the last step (a confirmed PO that is being
  // delivered) has every step done — not every step pending.
  const lifecycle: readonly string[] = ORDER_LIFECYCLE
  const pastEnd =
    !!opts?.upTo &&
    !base.includes(current) &&
    (current === "CLOSED" || lifecycle.indexOf(current) > lifecycle.indexOf(opts.upTo))
  return { steps, currentStatus: current, pastEnd }
}
